<template>
  <GcBaseLayout
    :title="title"
    :isXSmall="isXSmall"
    :isLarge="isLarge"
    :baseView="baseView"
    :key="pageKey"
    :backButtonEnable="backButtonRoute ? true : false"
    :backButtonFunction="backButtonFunction"
    :formsConfig="formsConfig"
    :tabs="getTabs"
    :selectedIndex="getSelectedIndex"
    ref="gc-base-layout"
    class="complaint-detail-layout"
  >
    <template #headerContent>
      <GcHeaderContentDynamic
        :title="'Customer Complaint'"
        :createModeIsEditMode="true"
        :baseView="baseView"
        :primaryLabel="
          baseView.mode == 'create'
            ? 'New Complaint'
            : 'Complaint #' + baseView.id
        "
        :secondaryLabel="dataModel.CustomerName"
        v-if="loaded"
      />
    </template>

    <div class="css-v2 transaction-screen" v-if="loaded">
      <div class="gc-header-toolbar">
        <gc-form-header-toolbar
          :toolbar-options="getHeaderToolbar"
          :data-model="dataModel"
          :itemSaveCallBack="onSaveCallback"
          :backRoute="backButtonRoute"
          :setLoading="showLoading"
          ref="gc-form-header-toolbar"
          :baseView="baseView"
        ></gc-form-header-toolbar>
      </div>

      <div class="gc-header-form">
        <div class="header-form-container">
          <div class="view-card responsive-paddings complaint-header-card">
            <gc-form
              :formOptions="headerFormOptions"
              :formItems="headerFormItems"
              :isNormal="true"
              :data-model="dataModel"
              :formMode="baseView.mode"
              :fromFrom="'complaint-header-form'"
              ref="complaint-header-form"
              :checkValid="isValid"
              :validationGroup="'complaint-header-form'"
              :scrollingEnabled="false"
            ></gc-form>
          </div>
        </div>
      </div>

      <div class="complaint-content-tabs">
        <DxToolbar class="complaint-tab-toolbar" v-if="loaded">
          <DxItem
            :options="{
              width: 'auto',
              dataSource: getTabViews,
              selectedIndex: selectedTabIndex,
              onItemClick: handleTabClick,
            }"
            location="before"
            widget="dxTabs"
          />
        </DxToolbar>

        <div class="complaint-tab-content">
          <div v-if="selectedTabIndex === getTabIndex('products')" class="tab-pane">
            <complaint-lines-tab
              ref="linesTab"
              :lines="dataModel.Lines"
              :customer-code="dataModel.CustomerCode"
              :is-read-only="isLinesReadOnly"
              :complaint-type-code="dataModel.ComplaintTypeCode"
              :root-cause-code="dataModel.Investigation && dataModel.Investigation.RootCauseClassificationCode ? dataModel.Investigation.RootCauseClassificationCode : null"
            />
          </div>
          <div v-if="selectedTabIndex === getTabIndex('investigation')" class="tab-pane">
            <complaint-investigation-tab
              :investigation="dataModel.Investigation"
              :is-read-only="isReview"
              @root-cause-changed="onRootCauseChanged"
            />
          </div>
          <div v-if="selectedTabIndex === getTabIndex('actions')" class="tab-pane">
            <complaint-actions-tab
              ref="actionsTab"
              :actions="dataModel.Actions"
              :is-read-only="isActionsReadOnly"
            />
          </div>
          <div v-if="selectedTabIndex === getTabIndex('attachments')" class="tab-pane">
            <complaint-attachments-tab
              :attachments="dataModel.TransactionMediaMetas"
              :is-read-only="isAttachmentsReadOnly"
              @change="onAttachmentsChange"
            />
          </div>
        </div>
      </div>
    </div>
  </GcBaseLayout>
</template>

<script>
import RouteMixin from "@/_mixins/route.mixin";
import FormMixin from "@/_mixins/form.mixin";
import validationEngine from "devextreme/ui/validation_engine";
import { CustomerComplaintService } from "@/_services/api/quality-control/customer-complaint.service";
import { CustomerService } from "@/_services/api/customers/customer.service";
import ComplaintInvestigationTab from "./complaint-investigation-tab.vue";
import ComplaintActionsTab from "./complaint-actions-tab.vue";
import ComplaintAttachmentsTab from "./complaint-attachments-tab.vue";
import ComplaintLinesTab from "./complaint-lines-tab.vue";
import { DxToolbar, DxItem } from "devextreme-vue/toolbar";
import { FeatureStorage } from "@/_services/storage";

const COMPLAINT_TABS = [
  { id: "products", text: "Products" },
  { id: "investigation", text: "Investigation & Root Cause" },
  { id: "actions", text: "Corrective & Preventative Actions" },
  { id: "attachments", text: "Attachments" },
];

export default {
  mixins: [RouteMixin, FormMixin],
  components: {
    DxToolbar,
    DxItem,
    ComplaintInvestigationTab,
    ComplaintActionsTab,
    ComplaintAttachmentsTab,
    ComplaintLinesTab,
  },
  data() {
    return {
      loaded: false,
      backRoute: "/customer-complaints",
      dataModel: {
        ComplaintCode: undefined,
        CustomerCode: undefined,
        CustomerName: undefined,
        DateRaised: new Date(),
        ComplaintTypeCode: undefined,
        Description: undefined,
        Status: "Open",
        ChangedBy: undefined,
        CreatedBy: undefined,
        DateChanged: undefined,
        DateCreated: undefined,
        Investigation: {
          InvestigationNotes: null,
          RootCauseClassificationCode: null,
          RootCauseMethodology: null,
        },
        Lines: [],
        Actions: [],
        TransactionMediaMetas: [],
      },
      isValid: false,
      selectedTabIndex: 0,
      headerFormOptions: {
        gcLabelLocation: "left",
      },
      rawComplaint: null,
    };
  },
  async created() {
    this.showLoading(true);
    if (this.baseView.mode !== "create") {
      await this.loadComplaint();
    }
    this.loaded = true;
    this.showLoading(false);
    if (this.baseView.mode === "create") {
      var self = this;
      this.$nextTick(function() {
        setTimeout(function() {
          self.focusCustomerField();
        }, 300);
      });
    }
  },
  watch: {
    "baseView.mode": function(newMode) {
      if (newMode === "edit") {
        var self = this;
        this.$nextTick(function() {
          setTimeout(function() {
            self.focusCustomerField();
          }, 300);
        });
      }
    },
  },
  computed: {
    pageKey() {
      return `complaint-${this.baseView.mode}-${this.baseView.id || "new"}`;
    },
    backButtonRoute() {
      return this.backRoute;
    },
    getTabs() {
      return [];
    },
    getSelectedIndex() {
      return 0;
    },
    isReview() {
      return this.baseView.mode === "review";
    },
    isNewComplaint() {
      return this.baseView.mode === "create";
    },
    getTabViews() {
      var tabs = this.isNewComplaint
        ? COMPLAINT_TABS.filter(function (t) {
            return t.id === "products" || t.id === "attachments";
          })
        : COMPLAINT_TABS;
      return tabs.map(function (t) {
        return { text: t.text, id: t.id };
      });
    },
    isEditable() {
      var savedStatus = this.rawComplaint ? this.rawComplaint.Status : "Open";
      return  (savedStatus === "Open" || savedStatus === "Investigating") && 
            (FeatureStorage.checkPermission(
              "CustomerComplaints",
              "create",
            ));
    },
    statusDropdownItems() {
      var status = this.rawComplaint ? this.rawComplaint.Status : "Open";
      if (status === "Open") {
        return [
          { value: "Open", text: "Open" },
          { value: "Investigating", text: "Investigating" },
          { value: "Cancelled", text: "Cancelled" },
        ];
      }
      if (status === "Investigating") {
        return [
          { value: "Investigating", text: "Investigating" },
          { value: "Open", text: "Open" },
          { value: "Resolved", text: "Resolved" },
          { value: "Cancelled", text: "Cancelled" },
        ];
      }
      return [];
    },
    isLinesReadOnly() {
      return this.isReview || !this.isEditable;
    },
    isActionsReadOnly() {
      return this.isReview || !this.isEditable;
    },
    isAttachmentsReadOnly() {
      return this.isReview || !this.isEditable;
    },
    statusActionButtons() {
      var self = this;
      var isReview = self.baseView.mode === "review";
      var status = self.dataModel.Status;
      return [
        {
          text: "Investigate",
          type: "default",
          visible: isReview && status === "Open",
          onClick: function () {
            self.changeStatus(
              "Investigating",
              "Are you sure you want to start investigating this complaint?",
            );
          },
        },
        {
          text: "Resolve",
          type: "success",
          visible: isReview && status === "Investigating",
          onClick: function () {
            self.changeStatus(
              "Resolved",
              "Are you sure you want to mark this complaint as resolved?",
            );
          },
        },
        {
          text: "Cancel Complaint",
          type: "danger",
          visible:
            isReview && (status === "Open" || status === "Investigating"),
          onClick: function () {
            self.changeStatus(
              "Cancelled",
              "Are you sure you want to cancel this complaint?",
            );
          },
        },
        {
          text: "Reopen",
          type: "normal",
          visible:
            isReview && (status === "Resolved" || status === "Cancelled"),
          onClick: function () {
            var target =
              self.dataModel.Status === "Resolved" ? "Investigating" : "Open";
            self.changeStatus(
              target,
              "Are you sure you want to reopen this complaint?",
            );
          },
        },
      ];
    },
    getHeaderToolbar() {
      var isEdit = this.baseView.mode === "edit";
      var isReview = this.baseView.mode === "review";
      return {
        screenName: "Customer Complaint",
        audit: this.baseView.mode === "review",
        status: !isEdit,
        notes: false,
        save: isReview ? this.isEditable : true,
        editURL: "/customer-complaints",
        auditItems: [
          {
            itemType: "group",
            caption: "",
            items: [
              {
                dataField: "FormattedDateCreated",
                label: { text: "Created By", location: "left" },
                editorType: "dxTextBox",
                editorOptions: {
                  disabled: true,
                },
              },
              {
                dataField: "FormattedDateChanged",
                label: { text: "Changed By", location: "left" },
                editorType: "dxTextBox",
                editorOptions: {
                  disabled: true,
                },
              },
            ],
          },
        ],
        toolbarAfterFormItems: isEdit && this.isEditable
          ? [
              {
                dataField: "Status",
                editorType: "dxSelectBox",
                label: { visible: false },
                editorOptions: {
                  items: this.statusDropdownItems,
                  displayExpr: "text",
                  valueExpr: "value",
                  width: 160,
                },
              },
            ]
          : null,
        customButtons: this.statusActionButtons,
      };
    },
    headerFormItems() {
      var self = this;
      return [
        {
          itemType: "group",
          colCount: 2,
          cssClass: "complaint-header-group",
          items: [
            {
              screenField: "Customer",
              dataField: "CustomerCode",
              displayExpr: function (item) {
                return item ? item.Code + " - " + item.Name : "";
              },
              searchExpr: ["Code", "Name"],
              editorOptions: {
                disabled: self.isReview,
                placeholder: "Select customer...",
                showClearButton: true,
                onValueChanged: function (e) {
                  self.dataModel.CustomerCode = e.value;
                  if (e.selectedItem) {
                    self.dataModel.CustomerName = e.selectedItem.Name;
                  } else {
                    self.dataModel.CustomerName = undefined;
                  }
                },
              },
              required: true,
            },
            {
              dataField: "DateRaised",
              label: { text: "Date Raised" },
              editorType: "dxDateBox",
              editorOptions: {
                max: new Date(),
                type: "date",
                displayFormat: "dd MMM yyyy",
                disabled: self.isReview,
              },
              validationRules: [
                { type: "required", message: "Date Raised is required" },
              ],
            },
            {
              screenField: "ComplaintType",
              dataField: "ComplaintTypeCode",
              label: { text: "Complaint Type" },
              displayExpr: function (item) {
                return item ? item.Code + " - " + item.Description : "";
              },
              valueExpr: "Code",
              searchExpr: ["Code", "Description"],
              required: true,
              editorOptions: {
                disabled: self.isReview,
                placeholder: "Select type...",
                showClearButton: true,
                onValueChanged: function (e) {
                  self.onComplaintTypeChanged(e.value);
                },
              },
            },
          ],
        },
        {
          itemType: "group",
          colCount: 2,
          cssClass: "complaint-header-group",
          items: [
            {
              dataField: "Description",
              label: { text: "Description" },
              editorType: "dxTextArea",
              colSpan: 2,
              cssClass: "textarea-item",
              editorOptions: {
                placeholder: "Describe the complaint...",
                disabled: self.isReview,
                maxLength: 1000,
              },
            },
          ],
        },
      ];
    },
  },
  methods: {
    focusCustomerField() {
      var formRef = this.$refs["complaint-header-form"];
      if (formRef) {
        var editor = formRef.getEditorField("CustomerCode");
        if (editor) editor.focus();
      }
    },
    handleTabClick(e) {
      this.selectedTabIndex = e.itemIndex;
    },
    getTabIndex(id) {
      var tabs = this.getTabViews;
      for (var i = 0; i < tabs.length; i++) {
        if (tabs[i].id === id) return i;
      }
      return -1;
    },
    async onComplaintTypeChanged(newCode) {
      var lines = this.dataModel.Lines;
      if (!lines || lines.length === 0) return;
      var hasLines = lines.some(function(l) {
        return l.ComplaintTypeCode !== newCode;
      });
      if (!hasLines) return;
      var confirmed = await this.$confirm(
        'Would you like to update the Complaint Type on all product lines to match?',
        'Update Lines'
      );
      if (confirmed && this.$refs.linesTab) {
        this.$refs.linesTab.applyComplaintType(newCode);
      }
    },
    onRootCauseChanged(newCode) {
      var lines = this.dataModel.Lines;
      if (!lines || lines.length === 0) return;
      lines.forEach(function(l) {
        if (!l.RootCauseClassificationCode) {
          l.RootCauseClassificationCode = newCode;
        }
      });
    },
    onAttachmentsChange(files) {
      this.dataModel.TransactionMediaMetas = files;
    },
    buildAttachmentsPayload() {
      return this.dataModel.TransactionMediaMetas.map(function (f) {
        return {
          RecordId: f.RecordId || '00000000-0000-0000-0000-000000000000',
          TransactionRecordId: "00000000-0000-0000-0000-000000000000",
          TransactionType: 'CustomerComplaint',
          ContentType: f.ContentType,
          FileName: f.FileName,
          FileSize: f.FileSize,
          MediaBase64String: f.MediaBase64String || undefined,
        };
      });
    },
    buildActionsPayload() {
      return this.dataModel.Actions.map(function (a) {
        return {
          RecordId: a.RecordId || null,
          SequenceNumber: a.SequenceNumber || 0,
          Description: a.Description,
          Type: a.Type,
          Owner: a.Owner,
          DueDate: a.DueDate ? new Date(a.DueDate).toSaveDate() : null,
          Status: a.Status || "Open",
          FollowUpReviewed: a.FollowUpReviewed || false,
          TargetFollowUpDate: a.TargetFollowUpDate
            ? new Date(a.TargetFollowUpDate).toSaveDate()
            : null,
        };
      });
    },
    buildLinesPayload() {
      return (this.dataModel.Lines || []).map(function(l) {
        return {
          RecordId: l.RecordId || null,
          ProductCode: l.ProductCode || '',
          DeliveryNote: l.DeliveryNote || '',
          DeliveryNoteLine: l.DeliveryNoteLine || '',
          InvoiceNo: l.InvoiceNo || '',
          Batch: l.Batch || '',
          WorkOrder: l.WorkOrder || '',
          PackingLine: l.PackingLine || '',
          ComplaintTypeCode: l.ComplaintTypeCode || null,
          RootCauseClassificationCode: l.RootCauseClassificationCode || null,
          Description: l.Description || '',
        };
      });
    },
    buildInvestigationPayload() {
      var inv = this.dataModel.Investigation;
      if (!inv) return null;
      var hasData = inv.InvestigationNotes || inv.RootCauseClassificationCode || inv.RootCauseMethodology;
      if (!hasData) return null;
      return {
        InvestigationNotes: inv.InvestigationNotes || null,
        RootCauseClassificationCode: inv.RootCauseClassificationCode || null,
        RootCauseMethodology: inv.RootCauseMethodology || null,
      };
    },
    backButtonFunction() {
      this.$router.push(this.backButtonRoute);
    },
    async loadComplaint() {
      try {
        var response = await CustomerComplaintService.getByCode(
          this.baseView.id,
        );
        var data = response.data;
        if (!data) return;
        this.rawComplaint = data;
        this.dataModel.ComplaintCode = data.ComplaintCode;
        this.dataModel.CustomerCode = data.CustomerCode;
        this.dataModel.CustomerName = data.CustomerName;
        this.dataModel.CreatedBy = data.CreatedBy;
        this.dataModel.ChangedBy = data.ChangedBy;
        this.dataModel.DateCreated = data.DateCreated;
        this.dataModel.DateChanged = data.DateChanged;
        if (!this.dataModel.CustomerName && this.dataModel.CustomerCode) {
          var custRes = await CustomerService.getByCode(this.dataModel.CustomerCode, "Code,Name");
          if (custRes && custRes.data && custRes.data.value && custRes.data.value.length > 0) {
            this.dataModel.CustomerName = custRes.data.value[0].Name;
          }
        }
        this.dataModel.DateRaised = data.DateRaised
          ? new Date(data.DateRaised)
          : undefined;
        this.dataModel.ComplaintTypeCode = data.ComplaintTypeCode;
        this.dataModel.Description = data.Description;
        this.dataModel.Status = data.Status || "Open";
        if (data.Investigation) {
          this.dataModel.Investigation = {
            InvestigationNotes: data.Investigation.InvestigationNotes || null,
            RootCauseClassificationCode: data.Investigation.RootCauseClassificationCode || null,
            RootCauseMethodology: data.Investigation.RootCauseMethodology || null,
          };
        }
        if (data.Lines && Array.isArray(data.Lines)) {
          this.dataModel.Lines = data.Lines.map(function(l) {
            return {
              RecordId: l.RecordId || null,
              ProductCode: l.ProductCode || '',
              ProductDescription: l.ProductDescription || '',
              DeliveryNote: l.DeliveryNote || '',
              DeliveryNoteLine: l.DeliveryNoteLine || '',
              InvoiceNo: l.InvoiceNo || '',
              Batch: l.Batch || '',
              WorkOrder: l.WorkOrder || '',
              PackingLine: l.PackingLine || '',
              ComplaintTypeCode: l.ComplaintTypeCode || null,
              RootCauseClassificationCode: l.RootCauseClassificationCode || null,
              Description: l.Description || '',
              IsBatchControlled: !!l.Batch,
              IsWorkOrderLinked: !!l.WorkOrder,
            };
          });
        } else {
          this.dataModel.Lines = [];
        }
        if (data.Actions && Array.isArray(data.Actions)) {
          this.dataModel.Actions = data.Actions.map(function (a) {
            return {
              RecordId: a.RecordId || null,
              SequenceNumber: a.SequenceNumber || 0,
              Description: a.Description || "",
              Type: a.Type || "Corrective",
              Owner: a.Owner || "",
              DueDate: a.DueDate ? new Date(a.DueDate) : null,
              Status: a.Status || "Open",
              FollowUpReviewed: a.FollowUpReviewed || false,
              TargetFollowUpDate: a.TargetFollowUpDate
                ? new Date(a.TargetFollowUpDate)
                : null,
            };
          });
        } else {
          this.dataModel.Actions = [];
        }
        if (data.Attachments && Array.isArray(data.Attachments)) {
          this.dataModel.TransactionMediaMetas = data.Attachments.map(function (f) {
            return {
              RecordId: f.RecordId || '00000000-0000-0000-0000-000000000000',
              TransactionRecordId: data.RecordId || '00000000-0000-0000-0000-000000000000',
              TransactionType: 'CustomerComplaint',
              FileName: f.FileName,
              BlobName: f.BlobName || null,
              ContentType: f.ContentType,
              FileSize: f.FileSize,
              MediaBase64String: null,
              MediaUrl: f.MediaUrl || null,
            };
          });
        } else {
          this.dataModel.TransactionMediaMetas = [];
        }
      } catch (error) {
        this.displayValidationErrors([
          "Failed to load complaint. Please try again.",
        ]);
      }
    },
    async changeStatus(newStatus, message) {
      var confirmed = await this.$confirm(message, "Confirm", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning",
      });
      if (!confirmed) return;
      await this.saveStatusChange(newStatus);
    },
    async saveStatusChange(newStatus) {
      this.showLoading(true);
      try {
        var raw = this.rawComplaint;
        var payload = {
          ComplaintCode: this.baseView.id,
          CustomerCode: raw.CustomerCode,
          DateRaised: raw.DateRaised,
          ComplaintTypeCode: raw.ComplaintTypeCode || null,
          Description: raw.Description || null,
          Status: newStatus,
          Lines: this.buildLinesPayload(),
          Investigation: this.buildInvestigationPayload(),
          Actions: this.buildActionsPayload(),
          TransactionMediaMetas: raw.TransactionMediaMetas || [],
        };
        await CustomerComplaintService.save(payload);
        this.dataModel.Status = newStatus;
        this.rawComplaint.Status = newStatus;
      } catch (error) {
        var messages = [];
        if (
          error &&
          error.data &&
          Array.isArray(error.data) &&
          error.data.length > 0
        ) {
          messages = error.data.map(function (e) {
            return e.ErrorMessage || "Unknown error";
          });
        } else if (error && error.data && error.data.Message) {
          messages = [error.data.Message];
        } else if (error && error.message) {
          messages = [error.message];
        } else {
          messages = ["Failed to update status. Please try again."];
        }
        this.displayValidationErrors(messages);
      } finally {
        this.showLoading(false);
      }
    },
    async onSaveCallback(isValid) {
      if (!isValid) return;
      var headerFormResult = validationEngine.validateGroup(
        "complaint-header-form",
      );
      if (!headerFormResult.isValid) {
        this.displayValidationSummary(headerFormResult.brokenRules);
        return;
      }
      if (this.$refs.linesTab) {
        await this.$refs.linesTab.commitEdits();
      }
      if (this.$refs.actionsTab) {
        await this.$refs.actionsTab.commitEdits();
      }
      await this.saveComplaint();
    },
    async saveComplaint() {
      this.showLoading(true);
      try {
        var payload = {
          ComplaintCode:
            this.baseView.mode === "create" ? null : this.baseView.id,
          CustomerCode: this.dataModel.CustomerCode,
          DateRaised: this.dataModel.DateRaised
            ? new Date(this.dataModel.DateRaised).toSaveDate()
            : undefined,
          ComplaintTypeCode: this.dataModel.ComplaintTypeCode || null,
          Description: this.dataModel.Description || null,
          Status: this.dataModel.Status || "Open",
          Lines: this.buildLinesPayload(),
          Investigation: this.buildInvestigationPayload(),
          Actions: this.buildActionsPayload(),
          TransactionMediaMetas: this.buildAttachmentsPayload(),
        };
        var response = await CustomerComplaintService.save(payload);
        this.dataModel.CustomerName = response && response.data && response.data.CustomerName;
        if (this.rawComplaint) {
          this.rawComplaint.Status = payload.Status;
        }
        this.ignoreRouteChangeConfirmation = true;
        if (this.baseView.mode === "create") {
          var newCode = response && response.data && response.data.ComplaintCode;
          var self = this;
          CustomerComplaintService.getByCode(newCode).then(function(fetchResponse) {
            var data = fetchResponse && fetchResponse.data;
            if (data) {
              self.dataModel.DateCreated = data.DateCreated;
              self.dataModel.DateChanged = data.DateChanged;
              self.dataModel.CreatedBy = data.CreatedBy;
              self.dataModel.ChangedBy = data.ChangedBy;
            }
          }).catch(function() {});
          this.$router.push("/customer-complaints/review/" + newCode);
        } else {
          this.$router.push("/customer-complaints/review/" + this.baseView.id);
        }
      } catch (error) {
        var messages = [];
        if (
          error &&
          error.data &&
          Array.isArray(error.data) &&
          error.data.length > 0
        ) {
          messages = error.data.map(function (e) {
            return e.ErrorMessage || "Unknown error";
          });
        } else if (error && error.data && error.data.Message) {
          messages = [error.data.Message];
        } else if (error && error.message) {
          messages = [error.message];
        } else {
          messages = ["Failed to save complaint. Please try again."];
        }
        this.displayValidationErrors(messages);
      } finally {
        this.showLoading(false);
      }
    },
  },
};
</script>

<style lang="scss">
.complaint-detail-layout {

  // .dx-toolbar {
  //   height: 40px;
  // }

  .complaint-header-card {
    margin-bottom: 0;
  }

  .textarea-item {
    .dx-field-item-label {
      vertical-align: top;
      padding-top: 7px;
    }
  }

  .gc-header-form {
    padding-bottom: 0 !important;
  }

  .complaint-content-tabs {
    margin-top: 10px;
    background-color: #fff;

    .dx-toolbar {
      height: 40px;

      .dx-toolbar-items-container
      {
        height: inherit !important;
      }
    }

    .complaint-tab-content {
      padding: 16px;
    }
  }
}
</style>
